import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useState } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';

const useUserUtils = () => {
  const { db } = useFirebase();

  const [loading, setLoading] = useState(false);

  const fetchUserById = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return { ...userData, id: userId };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user: ', error);
      console.log('userId', userId);
    }
  };

  const checkUserNameAvailable = async (userName) => {
    const localUserName = localUserNameRules(userName);
    if (!localUserName.valid) {
      return {
        valid: false,
        message: localUserName.errorMessage,
        suggestedUserName: false,
      };
    }
    const userRef = collection(db, 'users');
    const q = query(userRef, where('username', '==', userName.toLowerCase()));
    const querySnapshot = await getDocs(q);

    const invalidUsernameRef = collection(db, 'invalidUsernames');
    const invalidUsernameQuery = query(
      invalidUsernameRef,
      where('username', '==', userName.toLowerCase())
    );
    const invalidUsernameSnapshot = await getDocs(invalidUsernameQuery);

    const valid = querySnapshot.empty && invalidUsernameSnapshot.empty;
    let message = '';
    if (!invalidUsernameSnapshot.empty) {
      message = 'This username is not allowed';
    } else if (!valid) {
      message = 'Username is already taken';
    } else {
      message = 'Username is available';
    }

    const suggestedUserName = !valid ? await getSuggestedUserName(userName) : userName;

    return {
      valid,
      message,
      suggestedUserName,
    };
  };

  const localUserNameRules = (userName) => {
    // Check for minimum length
    if (userName.length < 3) {
      return {
        valid: false,
        errorMessage: 'Username must be at least 3 characters long',
      };
    }

    // Check for spaces
    if (userName.includes(' ')) {
      return {
        valid: false,
        errorMessage: 'Username cannot contain spaces',
      };
    }

    // Check for invalid characters
    if (!/^[a-zA-Z0-9_]*$/.test(userName)) {
      return {
        valid: false,
        errorMessage: 'Username can only contain letters, numbers, and underscores',
      };
    }

    // If all checks pass
    return {
      valid: true,
      errorMessage: '',
    };
  };

  const checkUserNameAvailableHandler = async (userName) => {
    setLoading(true);
    const result = await checkUserNameAvailable(userName);
    setLoading(false);
    return result;
  };

  const getSuggestedUserName = async (userName, attempt = 1) => {
    const suggestedName = attempt === 1 ? userName : `${userName}_${attempt}`;

    const userRef = collection(db, 'users');
    const q = query(userRef, where('username', '==', suggestedName));
    const querySnapshot = await getDocs(q);

    // If username exists, recursively try the next number
    if (!querySnapshot.empty) {
      return getSuggestedUserName(userName, attempt + 1);
    }

    return suggestedName;
  };

  return {
    fetchUserById,
    checkUserNameAvailable: checkUserNameAvailableHandler,
    loading,
  };
};

export { useUserUtils };
